import React from 'react';
import AnimatedElement, { StaggerContainer, StaggerItem } from '../ui/AnimatedElement';

const Experience: React.FC = () => {
  const experiences = [
    {
      title: 'VP of Product Development',
      company: 'CertFID',
      period: 'Mar 2025 - Present',
      location: 'Houston, TX (Hybrid)',
      logo: `${process.env.PUBLIC_URL}/certfid-logo.jpg`,
      current: true,
      responsibilities: [
        'Led product development from concept to production',
        'Designed & architected full-stack software systems',
        'Implemented quality control and testing standards',
      ],
    },
    {
      title: 'Sourcing & Procurement Associate',
      company: 'Dalf Eti',
      period: 'Feb 2025 - Aug 2025',
      location: 'Houston, TX',
      logo: `${process.env.PUBLIC_URL}/dalf-eti-logo.png`,
      current: false,
      responsibilities: [
        'Secured $80K+ in government contracts',
        'Managed vendor relationships and RFQ processes',
        'Conducted supplier negotiations and cost analysis',
        'Streamlined procurement workflows with automation',
      ],
    },
    {
      title: 'Technical Support Representative',
      company: 'Shockbyte',
      period: '2019 - 2020',
      location: 'Remote',
      logo: `${process.env.PUBLIC_URL}/Shockbyte-Logo.jpeg`,
      current: false,
      responsibilities: [
        'Provided technical support for game server hosting customers',
        'Troubleshot Linux server issues and configurations',
        'Assisted with Minecraft, Ark, and other game server setups',
        'Maintained high customer satisfaction ratings',
      ],
    },
  ];

  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Atmospheric Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />

      {/* Subtle decorative gradient */}
      <div
        className="absolute top-0 left-1/4 w-1/2 h-1/2 opacity-20"
        style={{
          background: 'radial-gradient(ellipse at top, rgba(74, 103, 65, 0.1) 0%, transparent 60%)',
        }}
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.012]"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0,0,0,0.4) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col justify-center p-4 md:p-8">
        <AnimatedElement variant="fadeUp" className="mb-10">
          <span className="text-neutral-500 text-sm font-mono tracking-wide">03 — EXPERIENCE</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
            Professional Journey
          </h2>
        </AnimatedElement>

        <StaggerContainer className="space-y-5">
          {experiences.map((exp, index) => (
            <StaggerItem key={index}>
              <div className="group relative">
                {/* Timeline connector line */}
                {index < experiences.length - 1 && (
                  <div className="absolute left-7 top-20 bottom-0 w-px bg-gradient-to-b from-slate-200 to-transparent translate-y-4" />
                )}

                {/* Current role accent */}
                {exp.current && (
                  <div className="absolute -left-2 top-4 bottom-4 w-1 bg-gradient-to-b from-matcha-500 to-matcha-300 rounded-full" />
                )}

                <div
                  className={`relative p-5 rounded-2xl border transition-all duration-300 ${
                    exp.current
                      ? 'bg-gradient-to-br from-white to-matcha-50/30 border-matcha-200/60 shadow-md hover:shadow-xl hover:border-matcha-300'
                      : 'bg-white/80 backdrop-blur-sm border-slate-200/80 hover:bg-white hover:shadow-lg hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Logo with timeline dot */}
                    <div className="relative shrink-0">
                      <div
                        className={`absolute -left-[3px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm ${
                          exp.current ? 'bg-matcha-500' : 'bg-slate-300'
                        }`}
                        style={{ left: '-13px' }}
                      />
                      <div
                        className={`p-1 rounded-xl transition-all duration-300 ${
                          exp.current
                            ? 'bg-white shadow-md ring-1 ring-matcha-100'
                            : 'bg-white shadow-sm ring-1 ring-slate-100 group-hover:shadow-md'
                        }`}
                      >
                        <img
                          src={exp.logo}
                          alt={exp.company}
                          className="w-12 h-12 rounded-lg object-contain"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                        <div>
                          <h3 className="font-semibold text-slate-900 text-lg leading-tight">
                            {exp.title}
                          </h3>
                          <p className={`text-sm font-medium ${exp.current ? 'text-matcha-600' : 'text-slate-500'}`}>
                            {exp.company}
                          </p>
                        </div>
                        {exp.current && (
                          <span className="self-start px-3 py-1 rounded-full text-xs font-semibold bg-matcha-500 text-white shadow-sm">
                            Current
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-sm text-slate-400 mb-3">
                        <span className="font-mono text-xs">{exp.period}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span>{exp.location}</span>
                      </div>

                      {/* Responsibilities */}
                      <div className="space-y-1.5">
                        {exp.responsibilities.map((resp, idx) => (
                          <div key={idx} className="flex gap-3 text-sm text-slate-600">
                            <span
                              className={`mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                                exp.current ? 'bg-matcha-400' : 'bg-slate-300'
                              }`}
                            />
                            <span className="leading-relaxed">{resp}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </div>
  );
};

export default Experience;
